import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { Day, Levelbucket } from 'lumen/models';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';
import { DEMAND_INDEX_LEVEL_BUCKET_IDS } from 'lumen/utils/constants/market-insight-constants';
import convertIndexToMarketInsightDemandLevel from 'lumen/utils/market-insight/index-to-color-level';

interface LosRestriction {
  los: number;
  value: number;
}

export default class Summary extends Model {
  [Type] = 'summary' as const;

  @belongsTo('day', { async: false, inverse: 'summaries' })
  declare day: Day;

  @attr('number') declare availability: number;
  // Market insight glossary (handy for the values below):
  // https://docs.google.com/document/d/1C-hKkjbrG714aeiu5EpLYMQ4-lysL-PlYCfCyYuVBts/edit#heading=h.e4lypal9glfy
  @attr('number') declare availabilityBenchmark: number;
  @attr('string') declare benchmarkMessage: string;
  @attr('string') declare dateAggregation: string;
  // (updated submarket rate index)
  @attr('number') declare demandIndex: number;
  // (updated submarket benchmark)
  @attr('number') declare demandIndexBenchmark: number;
  // fallback for demandIndex (updated market rate index)
  @attr('number') declare destinationDemandIndex: number;
  // fallback for demandIndexBenchmark (updated market benchmark)
  @attr('number') declare destinationDemandIndexBenchmark: number;
  @attr('number') declare destinationRateIndex: number;
  @attr('dayjs') declare extractDate: NonNullable<DayjsTransformReturn>;
  @attr('boolean') declare isLatest: boolean;
  @attr() declare losRestrictions: LosRestriction[];
  @attr('string') declare message: string;
  @attr('string') declare rateIndexMessage: string;
  // Price strategy section benchmark
  @attr('number') declare smartCompsetRateIndex: number;

  @service declare intl: Services['intl'];
  @service declare miDemand: Services['mi-demand'];
  @service declare miSelections: Services['mi-selections'];
  @service declare store: Services['store'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get absoluteFormattedDemandIndexPercentage() {
    if (this.isDemandIndexMissing) {
      return this.intl.t('general.missing');
    }

    return `${Math.abs(Math.round(this.demandIndex * 100))}%`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get absoluteFormattedDestinationDemandIndexPercentage() {
    return `${Math.abs(Math.round(this.destinationDemandIndex * 100))}%`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availabilityBenchmarkPercentageNumber() {
    return Math.round(this.availabilityBenchmark * 100);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandIndexWithFallbackToDestinationDemandIndex() {
    if (!this.isDemandIndexMissing) {
      return this.demandIndex;
    }

    return this.destinationDemandIndex;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelColorScaleNumber() {
    let levelNumber = null;

    const demandIndexBucket = this.store.peekRecord<Levelbucket>(
      'levelbucket',
      DEMAND_INDEX_LEVEL_BUCKET_IDS.YEARLY,
    );

    // where there is not enough data from smart compset in the calendar we fall back to city demand (handled in demandIndexWithFallbackToDestinationDemandIndex)
    levelNumber = convertIndexToMarketInsightDemandLevel(
      this.demandIndexWithFallbackToDestinationDemandIndex,
      demandIndexBucket,
    );

    return levelNumber;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelColorScaleNumberDemandIndexBenchmark() {
    let levelNumber = null;

    // First check for this status message. If this is missing we can't show anything useful here
    if (!this.isBenchmarkDemandIndexMissing) {
      const demandIndexBucket = this.store.peekRecord<Levelbucket>(
        'levelbucket',
        DEMAND_INDEX_LEVEL_BUCKET_IDS.YEARLY,
      );

      // Where there is not enough data from smart compset in the calendar we fall back to city demand
      if (!this.isDemandIndexMissing) {
        levelNumber = convertIndexToMarketInsightDemandLevel(
          this.demandIndexBenchmark,
          demandIndexBucket,
        );
      } else if (!this.isBenchmarkDestinationDemandIndexMissing) {
        levelNumber = convertIndexToMarketInsightDemandLevel(
          this.destinationDemandIndexBenchmark,
          demandIndexBucket,
        );
      }
    }

    return levelNumber;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelColorScaleNumberRefPeriod() {
    let levelNumber = null;

    // Do the same demand color scale conversion as the original summary
    if (this.refPeriodSummary) {
      const demandIndexBucket = this.store.peekRecord<Levelbucket>(
        'levelbucket',
        DEMAND_INDEX_LEVEL_BUCKET_IDS.YEARLY,
      );

      // where there is not enough data from smart compset in the calendar we fall back to city demand (handled in demandIndexWithFallbackToDestinationDemandIndex)
      levelNumber = convertIndexToMarketInsightDemandLevel(
        this.refPeriodSummary.demandIndexWithFallbackToDestinationDemandIndex,
        demandIndexBucket,
      );
    }

    return levelNumber;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelLabel() {
    return this.intl.t(
      `market_insight.rate_level.level${this.demandLevelColorScaleNumber}`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelLabelRefPeriod() {
    return this.intl.t(
      `market_insight.rate_level.level${this.demandLevelColorScaleNumberRefPeriod}`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedAvailabiltyPercentageNumber() {
    return Math.round(this.availability * 100);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDemandIndexPercentage() {
    if (this.isDemandIndexMissing) {
      return this.intl.t('general.missing');
    }

    return `${this.demandIndex > 0 ? '+' : ''}${Math.round(this.demandIndex * 100)}%`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDestinationDemandIndexPercentage() {
    return `${this.destinationDemandIndex > 0 ? '+' : ''}${Math.round(
      this.destinationDemandIndex * 100,
    )}%`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCorrectDemandIndexValue() {
    // In these cases we don't want to show the percentages (e.g. in the tooltips)
    return !this.isDestinationDemandIndexMissing && !this.isDemandIndexMissing;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasDemandChange() {
    const demandIndexToUse =
      this.demandIndexWithFallbackToDestinationDemandIndex;
    const refPeriodDemandIndexToUse =
      this.refPeriodSummary?.demandIndexWithFallbackToDestinationDemandIndex ??
      0;

    return (
      this.demandLevelColorScaleNumber &&
      this.demandLevelColorScaleNumberRefPeriod &&
      // if there is no difference between the ref day and latest the demand have not changed
      this.demandLevelColorScaleNumber !==
        this.demandLevelColorScaleNumberRefPeriod &&
      // if the difference between the ref day and the latest is lower than the threshold, the change is too small to be considered as one and therefore it's not shown
      this.miDemand.demandChangeThreshold !== null &&
      Math.abs(demandIndexToUse - refPeriodDemandIndexToUse) >=
        this.miDemand.demandChangeThreshold
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBenchmarkDemandIndexMissing() {
    return this.benchmarkMessage === 'summary.demand_index_benchmark_missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBenchmarkDestinationDemandIndexMissing() {
    return (
      this.benchmarkMessage ===
      'summary.destination_demand_index_benchmark_missing'
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDailySummary() {
    return this.dateAggregation === DATE_AGGREGATION.DAY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDemandIndexMissing() {
    return this.message === 'summary.demand_index_missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDestinationDemandIndexMissing() {
    return this.message === 'summary.destination_demand_index_missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSummaryIncomplete() {
    return this.message === 'search_summary.incomplete_week';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSummaryMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isWeeklySummary() {
    return this.dateAggregation === DATE_AGGREGATION.WEEK;
  }

  // Used for the text in the explanation tooltip of the my price card benchmark bar
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceStrategyLevelColorScaleNumber() {
    let levelNumber = null;

    if (!this.isDemandIndexMissing) {
      const demandIndexBucket = this.store.peekRecord<Levelbucket>(
        'levelbucket',
        DEMAND_INDEX_LEVEL_BUCKET_IDS.YEARLY,
      );
      levelNumber = convertIndexToMarketInsightDemandLevel(
        this.smartCompsetRateIndex,
        demandIndexBucket,
      );
    }

    return levelNumber;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refPeriodSummary() {
    // Get the summary of the current day, but with an extract date of the selected reference days ago (instead of latest)
    let id = `${this.day.date.format('YYYY-MM-DD')},${
      this.miSelections.refDaysAgoDatesInAPIFormat[
        this.miSelections.selectedRefDaysAgo!.id
      ]
    }`;

    // Only append the date aggregation for weeks (API is backward compatible)
    if (this.dateAggregation === DATE_AGGREGATION.WEEK) {
      id = `${id},${this.dateAggregation}`;
    }

    return this.store.peekRecord<Summary>('summary', id);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get soldOutPercentageNumber() {
    // We need to calculate the sold out value ourselves
    let soldoutPercentage = 1 - this.formattedAvailabiltyPercentageNumber / 100;

    if (this.losRestrictions) {
      this.losRestrictions.forEach((losRestriction) => {
        soldoutPercentage -= losRestriction.value;
      });
    }

    return Math.round(soldoutPercentage * 100);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unavailableHotelsBenchmarkPercentageNumber() {
    return Math.round((1 - this.availabilityBenchmark) * 100);
  }

  // Value we show in the unavailable hotels card bar indicator should be the unavailable + all LOS restrictions (so basically everything that's not available)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unavailableHotelsPercentageNumber() {
    return Math.round((1 - this.availability) * 100);
  }
}
